import httpClient from '~/lib/http-client';

export interface FormSubmission {
  _id: string;
  landingPageId: string;
  formId: string;
  userId: string | null;
  fields: Record<string, unknown>;
  meta: {
    ip: string;
    userAgent: string;
    referrer: string;
  };
  createdAt: string;
}

export interface GetSubmissionsResponse {
  submissions: FormSubmission[];
  total: number;
  limit: number;
  skip: number;
}

export interface ExportResponse {
  csv: string;
  filename: string;
}

export type EmailProviderType = 'sendgrid' | 'mailgun' | 'postmark' | 'brevo' | 'resend';

export interface FormSettings {
  landingPageId: string;
  formId: string;
  emailProvider?: {
    provider: EmailProviderType;
    apiKey: string;
    domain?: string;
    fromEmail: string;
    fromName: string;
  };
  adminNotification?: {
    enabled: boolean;
    to: string;
    subject: string;
    message: string;
  };
  autoReply?: {
    enabled: boolean;
    replyToField: string;
    subject: string;
    message: string;
  };
  webhook?: {
    enabled: boolean;
    url: string;
    headers?: Record<string, string>;
  };
}

export async function apiGetFormSubmissions(
  deploymentId: string,
  options?: { formId?: string; limit?: number; skip?: number },
): Promise<GetSubmissionsResponse> {
  const params = new URLSearchParams();

  if (options?.formId) {
    params.set('formId', options.formId);
  }

  if (options?.limit) {
    params.set('limit', String(options.limit));
  }

  if (options?.skip) {
    params.set('skip', String(options.skip));
  }

  const { data } = await httpClient.get<GetSubmissionsResponse>(
    `/api/deployments/static/${deploymentId}/forms?${params}`,
  );

  return data;
}

export async function apiDeleteFormSubmission(deploymentId: string, submissionId: string): Promise<void> {
  await httpClient.delete(`/api/deployments/static/${deploymentId}/forms/${submissionId}`);
}

export async function apiExportFormSubmissions(deploymentId: string, formId?: string): Promise<ExportResponse> {
  const params = new URLSearchParams();

  if (formId) {
    params.set('formId', formId);
  }

  const { data } = await httpClient.get<ExportResponse>(
    `/api/deployments/static/${deploymentId}/forms/export?${params}`,
  );

  return data;
}

export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function apiGetFormIds(deploymentId: string): Promise<string[]> {
  const { data } = await httpClient.get<{ formIds: string[] }>(`/api/deployments/static/${deploymentId}/forms/ids`);

  return data.formIds;
}

export async function apiGetFormSettings(deploymentId: string, formId: string): Promise<FormSettings> {
  const { data } = await httpClient.get<{ settings: FormSettings }>(
    `/api/deployments/static/${deploymentId}/forms/${encodeURIComponent(formId)}/settings`,
  );

  return data.settings;
}

export async function apiSaveFormSettings(
  deploymentId: string,
  formId: string,
  settings: Partial<FormSettings>,
): Promise<void> {
  await httpClient.post(
    `/api/deployments/static/${deploymentId}/forms/${encodeURIComponent(formId)}/settings`,
    settings,
  );
}
